import { Component } from '@angular/core';
import { ComponentFixture, tick } from '@angular/core/testing';
import { FormControl } from '@angular/forms';
import { By } from '@angular/platform-browser';
import { ODataValue } from '../interfaces/odata-model.interface';

interface IndexableComponent extends Component {
    [index: string]: unknown;
}
interface ModelWithOutmodelContainer<T> extends IndexableComponent {
    model: {
        getOutModel: () => T;
    };
}

export const getElementByCssSelector = <T>(fixture: ComponentFixture<T>, selector: string): HTMLElement | undefined => {
    const input = fixture.debugElement.query(By.css(selector));
    return input ? <HTMLElement>input.nativeElement : undefined;
};

export const setInputValueByCssSelector = <T>(
    fixture: ComponentFixture<T>,
    selector: string,
    value: string | number | boolean | undefined,
): void => {
    const inputEl = <HTMLInputElement>getElementByCssSelector(fixture, selector);

    if (typeof inputEl === 'undefined') {
        throw new Error(`setInputValueByCssSelector: Could not find element for selector ${selector}`);
    }

    inputEl.value = typeof value === 'undefined' ? <string>(<unknown>value) : value.toString();
    inputEl.dispatchEvent(new Event('input'));

    fixture.detectChanges();
};

export const getFormControlForComponent = <T extends IndexableComponent>(component: T): FormControl | undefined => {
    const componentProperties = Object.getOwnPropertyNames(component);
    for (const componentProperty of componentProperties) {
        if (component[componentProperty] instanceof FormControl) {
            return component[componentProperty] as FormControl;
        }
    }

    return undefined;
};

export const checkElementValueFromComponent = <T>(fixture: ComponentFixture<T>, selector: string): void => {
    const component = fixture.componentInstance;
    const inputElement = <HTMLInputElement>getElementByCssSelector(fixture, selector);

    // check first if the Element is there.
    /* eslint-disable  */
    // @ts-ignore
    expect(inputElement).toBeDefined();
    // afterward check if the value of the DOM element is the same as the value from the model
    // @ts-ignore
    expect(inputElement.value).toBe(component.model.value.toString());
    /* eslint-enable  */
};

// checks if the value set to component is also set on the model
export const checkComponentValueOnModel = <T>(
    fixture: ComponentFixture<T>,
    selector: string,
    value: string | number | boolean,
): Promise<void> => {
    const component = fixture.componentInstance;
    setInputValueByCssSelector(fixture, selector, value);
    return fixture.whenStable().then(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        expect(component.model.value).toBe(value);
    });
};

// checks if the minLength validator is working
export const checkMinLengthValidator = <T extends IndexableComponent>(
    fixture: ComponentFixture<T>,
    selector: string,
    failingMinLengthValue: string,
): Promise<void> => {
    const component = fixture.componentInstance;
    setInputValueByCssSelector(fixture, selector, failingMinLengthValue);
    return fixture
        .whenStable()
        .then(() => {
            const formControl = getFormControlForComponent(component);
            const errorsFound = formControl ? formControl.errors : undefined;
            /* eslint-disable  */
            // @ts-ignore
            return expect(!!(errorsFound && errorsFound.minlength)).toBeTruthy();
            /* eslint-enable  */
        })
        .catch((error: Error) => {
            console.log(
                `Error in TestHelperFunctions -> checkMinLengthValidator -> fixture.whenStable: ${JSON.stringify(
                    error,
                )}`,
            );
        });
};

// checks if the maxLength validator is working
export const checkMaxLengthValidator = <T>(
    fixture: ComponentFixture<T>,
    selector: string,
    failingMaxLengthValue: string,
): Promise<void> => {
    const component = <IndexableComponent>fixture.componentInstance;
    setInputValueByCssSelector(fixture, selector, failingMaxLengthValue);
    return fixture
        .whenStable()
        .then(() => {
            const formControl = getFormControlForComponent(component);
            const errorsFound = formControl ? formControl.errors : undefined;
            /* eslint-disable  */
            // @ts-ignore
            return expect(!!(errorsFound && errorsFound.maxlength)).toBeTruthy();
            /* eslint-enable  */
        })
        .catch((error: Error) => {
            console.log(
                `Error in TestHelperFunctions -> checkMaxLengthValidator -> fixture.whenStable: ${JSON.stringify(
                    error,
                )}`,
            );
        });
};

// checks if the maxLength validator is working
export const checkMaxValueValidator = <T extends IndexableComponent>(
    fixture: ComponentFixture<T>,
    selector: string,
    failingMaxValue: number,
): Promise<void> => {
    const component = fixture.componentInstance;
    setInputValueByCssSelector(fixture, selector, failingMaxValue);
    return fixture
        .whenStable()
        .then(() => {
            const formControl = getFormControlForComponent(component);
            const errorsFound = formControl ? formControl.errors : undefined;
            /* eslint-disable  */
            // @ts-ignore
            return expect(!!(errorsFound && errorsFound.max)).toBeTruthy();
            /* eslint-enable  */
        })
        .catch((error: Error) => {
            console.log(
                `Error in TestHelperFunctions -> checkMaxValueValidator -> fixture.whenStable: ${JSON.stringify(error)}`,
            );
        });
};

// checks if the maxLength validator is working
export const checkMinValueValidator = <T extends IndexableComponent>(
    fixture: ComponentFixture<T>,
    selector: string,
    failingMinValue: number,
): Promise<void> => {
    const component = fixture.componentInstance;
    setInputValueByCssSelector(fixture, selector, failingMinValue);
    return fixture
        .whenStable()
        .then(() => {
            const formControl = getFormControlForComponent(component);
            const errorsFound = formControl ? formControl.errors : undefined;
            /* eslint-disable  */
            // @ts-ignore
            return expect(!!(errorsFound && errorsFound.min)).toBeTruthy();
            /* eslint-enable  */
        })
        .catch((error: Error) => {
            console.log(
                `Error in TestHelperFunctions -> checkMinValueValidator -> fixture.whenStable: ${JSON.stringify(error)}`,
            );
        });
};

// checks if the required validator is working
export const checkRequiredValidator = <T>(
    fixture: ComponentFixture<ModelWithOutmodelContainer<T>>,
    selector: string,
): Promise<void> => {
    const component = fixture.componentInstance;
    const requiredFailingValue = '';

    setInputValueByCssSelector(fixture, selector, requiredFailingValue);
    return fixture
        .whenStable()
        .then(() => {
            const formControl = getFormControlForComponent(component);
            const errorsFound = formControl ? formControl.errors : undefined;
            /* eslint-disable  */
            // @ts-ignore
            return expect(!!(errorsFound && errorsFound.required)).toBeTruthy();
            /* eslint-enable  */
        })
        .catch((error: Error) => {
            console.log(
                `Error in TestHelperFunctions -> checkRequiredValidator -> fixture.whenStable: ${JSON.stringify(error)}`,
            );
        });
};

// check if the value is set correctly in the OutModel
export const checkOutModelValue = <T>(
    fixture: ComponentFixture<ModelWithOutmodelContainer<T>>,
    selector: string,
    value: string | number | boolean | undefined,
    expectedValue: ODataValue,
): Promise<void> => {
    const component = fixture.componentInstance;

    setInputValueByCssSelector(fixture, selector, value);
    return fixture.whenStable().then(() => {
        const outModelValue = component.model.getOutModel();
        /* eslint-disable  */
        // @ts-ignore
        return expect(outModelValue[component.model.name]).toBe(expectedValue);
        /* eslint-enable  */
    });
};

export const selectDropdownValue = <T>(
    fixture: ComponentFixture<T>,
    selector: string,
    optionListSelector: string,
    elementNumber: number,
): void => {
    const inputElement = <HTMLInputElement>getElementByCssSelector(fixture, selector);
    inputElement.click();
    tick();
    fixture.detectChanges();

    // get the list of the dropdown li
    const list = <HTMLInputElement>getElementByCssSelector(fixture, optionListSelector);
    // need to trigger the click on the <a> element
    const firstElement = <HTMLInputElement>list.children[elementNumber].children[0];
    firstElement.click();
    tick();
    fixture.detectChanges();
};

export const selectRadioByIndex = <T>(fixture: ComponentFixture<T>, selector: string, selectionIndex: number): void => {
    const buttons = fixture.debugElement.queryAll(By.css(selector));
    const selectableRadioButton = <HTMLInputElement>buttons[selectionIndex].nativeElement;
    selectableRadioButton.click();
    fixture.detectChanges();
};

export const checkIfElementIsNonExisting = <T>(fixture: ComponentFixture<T>, selector: string): void => {
    const element = getElementByCssSelector(fixture, selector);
    /* eslint-disable  */
    // @ts-ignore
    expect(element).toBeUndefined();
    /* eslint-enable  */
};

export const checkIfElementIsExisting = <T>(fixture: ComponentFixture<T>, selector: string): void => {
    const element = getElementByCssSelector(fixture, selector);
    /* eslint-disable  */
    // @ts-ignore
    expect(element).toBeTruthy();
    /* eslint-enable  */
};

export const checkIfElementExistsAfterCondition = <T>(
    fixture: ComponentFixture<T>,
    selector: string,
): Promise<void> => {
    fixture.detectChanges();
    return fixture.whenStable().then(() => {
        const element = getElementByCssSelector(fixture, selector);
        /* eslint-disable  */
        // @ts-ignore
        expect(element).toBeTruthy();
        /* eslint-enable  */
    });
};
